import { useEffect, useState } from "react";
import { Loader2, Bot, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useLottery } from "@/hooks/use-lottery-selection";

export interface AiPoolFormValues {
  numbersPerGame: number;
  gameCount: number;
  hint: string;
  name: string;
  description: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (values: AiPoolFormValues) => Promise<void> | void;
  loading?: boolean;
  minPick: number;
  maxPick: number;
}

/**
 * Modal de geração de bolão com IA — mesmo padrão de UX do
 * Gerador Inteligente: pede números/jogo, quantidade e instrução opcional.
 */
export function AiPoolDialog({
  open,
  onOpenChange,
  onConfirm,
  loading,
  minPick,
  maxPick,
}: Props) {
  const { config } = useLottery();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [numbersPerGame, setNumbersPerGame] = useState<number>(config.pick);
  const [gameCount, setGameCount] = useState<number>(5);
  const [hint, setHint] = useState("");

  useEffect(() => {
    setNumbersPerGame((cur) =>
      Math.max(minPick, Math.min(maxPick, cur || config.pick)),
    );
  }, [minPick, maxPick, config.pick]);

  const submit = async () => {
    if (!name.trim()) return;
    await onConfirm({
      numbersPerGame: Math.max(minPick, Math.min(maxPick, numbersPerGame)),
      gameCount: Math.max(1, Math.min(10, gameCount)),
      hint: hint.trim(),
      name: name.trim(),
      description: description.trim(),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl font-light">
            <Bot className="h-5 w-5 text-primary" />
            Gerar bolão com IA
            <Badge
              variant="outline"
              className="border-primary/40 bg-primary/10 text-[10px] uppercase text-primary"
            >
              <Sparkles className="mr-1 h-3 w-3" /> Premium
            </Badge>
          </DialogTitle>
          <DialogDescription>
            A IA analisa o histórico recente da {config.name} e cria jogos
            balanceados para o seu bolão.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="ai-pool-name" className="text-xs">
              Nome do bolão
            </Label>
            <Input
              id="ai-pool-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Bolão da firma — Mega da virada"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ai-pool-numbers" className="text-xs">
                Números por jogo
              </Label>
              <Input
                id="ai-pool-numbers"
                type="number"
                min={minPick}
                max={maxPick}
                value={numbersPerGame}
                onChange={(e) =>
                  setNumbersPerGame(
                    Math.max(
                      minPick,
                      Math.min(maxPick, Number(e.target.value) || minPick),
                    ),
                  )
                }
              />
              <p className="text-[10px] text-muted-foreground">
                mín {minPick}, máx {maxPick}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ai-pool-count" className="text-xs">
                Quantidade de jogos
              </Label>
              <Input
                id="ai-pool-count"
                type="number"
                min={1}
                max={10}
                value={gameCount}
                onChange={(e) =>
                  setGameCount(
                    Math.max(1, Math.min(10, Number(e.target.value) || 1)),
                  )
                }
              />
              <p className="text-[10px] text-muted-foreground">de 1 a 10</p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ai-pool-hint" className="text-xs">
              Instrução opcional para a IA
            </Label>
            <Textarea
              id="ai-pool-hint"
              rows={2}
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              placeholder='Ex.: "priorize dezenas atrasadas" ou deixe vazio'
              maxLength={200}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ai-pool-desc" className="text-xs">
              Descrição do bolão (opcional)
            </Label>
            <Textarea
              id="ai-pool-desc"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Anotações para os cotistas"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={loading || !name.trim()}>
            {loading ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Bot className="mr-1.5 h-4 w-4" />
            )}
            Gerar bolão
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}